import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Map as MapLibreMap } from 'maplibre-gl';

interface CanvasContoursProps {
  map: MapLibreMap | null;
  visible: boolean;
  smoothLines?: boolean;
}

type ContourFeature = {
  type: 'Feature';
  geometry: { type: 'LineString'; coordinates: [number, number][] };
  properties: { depth: number; ID?: number };
};

type ContourFeatureCollection = {
  type: 'FeatureCollection';
  features: ContourFeature[];
  info?: {
    filtered?: number;
    zoom?: string;
    depthInterval?: string;
    queryTimeMs?: number;
  };
};

type Bounds = { west: number; south: number; east: number; north: number };

function boundsSpan(b: Bounds): { lon: number; lat: number } {
  return { lon: Math.abs(b.east - b.west), lat: Math.abs(b.north - b.south) };
}

function boundsMeters(b: Bounds, atLat: number): { widthM: number; heightM: number } {
  const { lon, lat } = boundsSpan(b);
  const latRad = (atLat * Math.PI) / 180;
  const metersPerLatDeg = 111_320;
  const metersPerLonDeg = Math.max(1e-6, metersPerLatDeg * Math.cos(latRad));
  return {
    widthM: lon * metersPerLonDeg,
    heightM: lat * metersPerLatDeg,
  };
}

function expandBoundsAboutCenter(b: Bounds, scale: number): Bounds {
  if (!Number.isFinite(scale) || scale <= 1) return b;
  const cx = (b.west + b.east) / 2;
  const cy = (b.south + b.north) / 2;
  const halfLon = Math.abs(b.east - b.west) / 2;
  const halfLat = Math.abs(b.north - b.south) / 2;
  const newHalfLon = halfLon * scale;
  const newHalfLat = halfLat * scale;
  return {
    west: clamp(cx - newHalfLon, -180, 180),
    east: clamp(cx + newHalfLon, -180, 180),
    south: clamp(cy - newHalfLat, -90, 90),
    north: clamp(cy + newHalfLat, -90, 90),
  };
}

function normalizeQueryWindow(
  raw: Bounds,
  centerLat: number,
  opts: {
    minWidthM: number;
    minHeightM: number;
    maxWidthM: number;
    maxHeightM: number;
  }
): Bounds {
  const cx = (raw.west + raw.east) / 2;
  const cy = (raw.south + raw.north) / 2;

  const { lon: lonSpan, lat: latSpan } = boundsSpan(raw);
  const safeLonSpan = Math.max(1e-6, lonSpan);
  const safeLatSpan = Math.max(1e-6, latSpan);

  const latRad = (centerLat * Math.PI) / 180;
  const metersPerLatDeg = 111_320;
  const metersPerLonDeg = Math.max(1e-6, metersPerLatDeg * Math.cos(latRad));

  const minLonDeg = opts.minWidthM / metersPerLonDeg;
  const maxLonDeg = opts.maxWidthM / metersPerLonDeg;
  const minLatDeg = opts.minHeightM / metersPerLatDeg;
  const maxLatDeg = opts.maxHeightM / metersPerLatDeg;

  const desiredLonSpan = clamp(safeLonSpan, minLonDeg, maxLonDeg);
  const desiredLatSpan = clamp(safeLatSpan, minLatDeg, maxLatDeg);

  const halfLon = desiredLonSpan / 2;
  const halfLat = desiredLatSpan / 2;

  return {
    west: clamp(cx - halfLon, -180, 180),
    east: clamp(cx + halfLon, -180, 180),
    south: clamp(cy - halfLat, -90, 90),
    north: clamp(cy + halfLat, -90, 90),
  };
}

function getDepthColor(depth: number): string {
  if (depth >= -20) return '#00FF00';
  if (depth >= -30) return '#00CCCC';
  if (depth >= -40) return '#0088FF';
  if (depth >= -50) return '#FFFF00';
  if (depth >= -60) return '#FF8800';
  if (depth >= -70) return '#FF4400';
  if (depth >= -80) return '#FF0000';
  if (depth >= -90) return '#CC0000';
  if (depth >= -100) return '#880000';
  if (depth >= -110) return '#790000ff';
  if (depth >= -140) return '#740000ff';
  if (depth >= -160) return '#700000ff';
  if (depth >= -180) return '#600000ff';
  if (depth >= -200) return '#500000ff';
  if (depth >= -240) return '#400000ff';
  if (depth >= -280) return '#300000ff';
  if (depth >= -300) return '#200000ff';
  return '#000000ff';
}

function getDepthModulo(zoom: number, bounds?: Bounds): number {
  // Keep in sync with server/bathymetry-sql-api.ts
  // Base zoom tiers.
  let mod: number;
  if (zoom < 7) mod = 100;
  else if (zoom < 8.5) mod = 40;
  else if (zoom < 11) mod = 20;
  else if (zoom < 11.5) mod = 10;
  else if (zoom < 13.5) mod = 4;
  else mod = 2;

  // If the viewport is physically small (degrees area), allow denser contours even
  // at mid zoom so local deep holes don't disappear due to coarse modulo.
  if (bounds) {
    const spanLon = Math.abs(bounds.east - bounds.west);
    const spanLat = Math.abs(bounds.north - bounds.south);
    const area = spanLon * spanLat;
    if (area < 0.0003) mod = 2;
    else if (area < 0.0007) mod = Math.min(mod, 4);
  }

  return mod;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function padBounds(bounds: Bounds, padFraction: number): Bounds {
  const width = bounds.east - bounds.west;
  const height = bounds.north - bounds.south;
  const padX = width * padFraction;
  const padY = height * padFraction;

  return {
    west: clamp(bounds.west - padX, -180, 180),
    east: clamp(bounds.east + padX, -180, 180),
    south: clamp(bounds.south - padY, -90, 90),
    north: clamp(bounds.north + padY, -90, 90),
  };
}

function snapBounds(bounds: Bounds, stepDeg: number): Bounds {
  // Snap outward to a grid to make bounds stable across small zoom changes.
  const snapDown = (v: number) => Math.floor(v / stepDeg) * stepDeg;
  const snapUp = (v: number) => Math.ceil(v / stepDeg) * stepDeg;

  return {
    west: clamp(snapDown(bounds.west), -180, 180),
    south: clamp(snapDown(bounds.south), -90, 90),
    east: clamp(snapUp(bounds.east), -180, 180),
    north: clamp(snapUp(bounds.north), -90, 90),
  };
}

function contains(outer: Bounds, inner: Bounds): boolean {
  return (
    inner.west >= outer.west &&
    inner.south >= outer.south &&
    inner.east <= outer.east &&
    inner.north <= outer.north
  );
}

export function CanvasContours({ map, visible, smoothLines = true }: CanvasContoursProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const showDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

  const [features, setFeatures] = useState<ContourFeature[]>([]);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);

  const fetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortDetailRef = useRef<AbortController | null>(null);
  const abortContextRef = useRef<AbortController | null>(null);
  const detailCacheRef = useRef<{ padded: Bounds; depthModulo: number } | null>(null);
  const contextCacheRef = useRef<{ padded: Bounds; depthModulo: number } | null>(null);
  const lastDetailQueryRef = useRef<{ count: number; ms: number; zoom: number; depthModulo: number } | null>(null);
  const lastContextQueryRef = useRef<{ count: number; ms: number; zoom: number; depthModulo: number } | null>(null);
  const lastQueryWindowRef = useRef<
    | {
        detail: { spanLon: number; spanLat: number };
        context: { spanLon: number; spanLat: number };
      }
    | null
  >(null);
  const lastQueryMetaRef = useRef<{ minDetailZoom: number; minContextZoom: number } | null>(null);
  const detailFeaturesRef = useRef<ContourFeature[]>([]);
  const contextFeaturesRef = useRef<ContourFeature[]>([]);
  const isInteractingRef = useRef(false);
  const pendingDrawRef = useRef(false);
  const awaitingFreshDataRef = useRef(false);
  const clearOverlayRef = useRef<() => void>(() => {});
  // Track when zoomend last fired so onMoveEnd (which always follows) can skip the
  // duplicate fetch. MapLibre fires zoomend then moveend ~1ms apart on every zoom;
  // without this guard both handlers start a SQL query, the second aborting the first.
  const lastZoomEndTimeRef = useRef<number>(0);

  // RULE: as you zoom in on the same area, never hide a contour that was visible before.
  // We implement this by keeping a “sticky” set of contour IDs that were accepted by the
  // thinning logic, and seeding future thinning runs with those IDs when zoom increases.
  const stickyContoursRef = useRef<{ key: string; maxZoom: number; ids: Set<number> }>({
    key: '',
    maxZoom: -Infinity,
    ids: new Set<number>(),
  });

  const drawNowRef = useRef<() => void>(() => {});
  type RequestFetch = (opts?: { immediate?: boolean }) => void;
  const requestFetchRef = useRef<RequestFetch>(() => {});

  const clearFetchCache = () => {
    detailCacheRef.current = null;
    contextCacheRef.current = null;
  };

  const canvasContainer = useMemo(() => {
    try {
      return map?.getCanvasContainer() ?? null;
    } catch {
      return null;
    }
  }, [map]);

  // Build the draw function whenever map/features/visible/smoothing changes.
  useEffect(() => {
    drawNowRef.current = () => {
      if (!map || !canvasRef.current || !visible) return;

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const mapCanvas = map.getCanvas();
      const container = mapCanvas;
      const dpr = window.devicePixelRatio || 1;
      const widthCss = Math.max(1, container.clientWidth);
      const heightCss = Math.max(1, container.clientHeight);
      const widthPx = Math.max(1, Math.floor(widthCss * dpr));
      const heightPx = Math.max(1, Math.floor(heightCss * dpr));

      if (canvas.width !== widthPx || canvas.height !== heightPx) {
        canvas.width = widthPx;
        canvas.height = heightPx;
        canvas.style.width = `${widthCss}px`;
        canvas.style.height = `${heightCss}px`;
      }

      // Match MapLibre canvas transform in case it isn't empty at rest.
      canvas.style.transform = mapCanvas.style.transform || '';
      canvas.style.transformOrigin = '0 0';

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, widthCss, heightCss);

      // Make polyline rendering look smoother (visual only; does not change geometry).
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.miterLimit = 2;

      if (features.length === 0) {
        if (showDebug) setDebugInfo((prev) => [...prev.slice(0, 4), 'Draw: 0 lines, 0 pts']);
        return;
      }

      // Safety cap: if the feature set is enormous (e.g. very large viewport at low zoom)
      // drawing synchronously stalls the browser main thread long enough to freeze RDP.
      // In that case bail early and let the user zoom in for a cleaner render.
      const MAX_DRAW_POINTS = 40_000;
      const totalPts = features.reduce((s, f) => s + (f.geometry.coordinates?.length ?? 0), 0);
      if (totalPts > MAX_DRAW_POINTS) {
        ctx.clearRect(0, 0, widthCss, heightCss);
        if (showDebug) setDebugInfo((prev) => [...prev.slice(0, 4), `Draw: skipped (${totalPts.toLocaleString()} pts > cap, zoom in)`]);
        return;
      }

      let drawnLines = 0;
      let totalPoints = 0;

      const labels: Array<{ x: number; y: number; angle: number; text: string; aabb: { minX: number; minY: number; maxX: number; maxY: number } }> = [];
      const placedLabelAabbs: Array<{ minX: number; minY: number; maxX: number; maxY: number }> = [];

      const zoom = map.getZoom();
      const shouldSmoothLines = smoothLines && zoom >= 11;

      // Track a stable-ish key for the current area so we can enforce monotonic visibility
      // while zooming in (but reset when you move to a different place or zoom out).
      const c = map.getCenter();
      const key = `${Math.round(c.lng * 100) / 100},${Math.round(c.lat * 100) / 100}`;
      const sticky = stickyContoursRef.current;
      const zoomedOut = zoom < sticky.maxZoom - 0.25;
      const moved = sticky.key !== '' && sticky.key !== key;
      if (moved || zoomedOut) {
        stickyContoursRef.current = { key, maxZoom: zoom, ids: new Set<number>() };
      } else if (sticky.key === '') {
        sticky.key = key;
        sticky.maxZoom = zoom;
      }
      const shouldDrawLabels = zoom >= 11;
      const labelSpacingPx = zoom >= 13 ? 350 : zoom >= 12 ? 450 : 650;
      const minLabelLineLengthPx = zoom >= 13 ? 220 : 320;

      const labelFont = 'bold 13px Noto Sans, sans-serif';
      const labelTextColor = '#003d82';
      const labelHaloColor = '#ffffff';
      const labelHaloWidth = 3;
      const labelKnockoutWidth = 6;
      const labelPaddingPx = 6;
      const labelMinTurnDeg = 28; // reject sharp bends

      // Adaptive thinning:
      // - Keep the deepest closed loops first (these indicate depressions / “fishing holes”).
      // - Thin only where contours are very close together in screen space.
      // At low zoom, contours are crowded → use a larger separation.
      // As you zoom in, reduce separation so you get *more* detail.
      const minSeparationPx =
        zoom >= 15 ? 4 :
        zoom >= 14 ? 5 :
        zoom >= 13 ? 6 :
        zoom >= 12 ? 8 :
        zoom >= 11 ? 8 :
        0;
      // As you zoom in, allow more distinct depth lines to coexist.
      const maxDepthDiffForSpacing =
        zoom >= 15 ? 3 :
        zoom >= 14 ? 4 :
        zoom >= 13 ? 5 :
        6;
      // As you zoom in, reduce “closed loop clustering” so concentric rings are less likely to thin out.
      const closedClusterRadiusPx =
        zoom >= 15 ? 55 :
        zoom >= 14 ? 60 :
        zoom >= 13 ? 65 :
        zoom >= 12 ? 70 :
        zoom >= 11 ? 75 :
        85;

      type ThinCandidate = {
        feature: ContourFeature;
        depth: number;
        isClosed: boolean;
        id: number | null;
        centroid: { x: number; y: number };
        bbox: { minX: number; minY: number; maxX: number; maxY: number };
        sample: Array<{ x: number; y: number }>;
        approxLen: number;
      };

      const isFinitePoint = (p: { x: number; y: number }) => Number.isFinite(p.x) && Number.isFinite(p.y);
      const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        return Math.sqrt(dx * dx + dy * dy);
      };

      const bboxIntersects = (a: ThinCandidate['bbox'], b: ThinCandidate['bbox'], pad: number) => {
        return !(a.maxX + pad < b.minX || a.minX - pad > b.maxX || a.maxY + pad < b.minY || a.minY - pad > b.maxY);
      };

      const minPointDistance = (
        aPts: Array<{ x: number; y: number }>,
        bPts: Array<{ x: number; y: number }>,
        earlyExit: number
      ) => {
        const ee2 = earlyExit * earlyExit;
        for (const ap of aPts) {
          for (const bp of bPts) {
            const dx = ap.x - bp.x;
            const dy = ap.y - bp.y;
            const d2 = dx * dx + dy * dy;
            if (d2 <= ee2) return Math.sqrt(d2);
          }
        }
        return Infinity;
      };

      const buildThinCandidates = (): ThinCandidate[] => {
        if (minSeparationPx <= 0) {
          return features.map((f) => ({
            feature: f,
            depth: f.properties.depth,
            isClosed: false,
            id: typeof f.properties.ID === 'number' ? f.properties.ID : null,
            centroid: { x: 0, y: 0 },
            bbox: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
            sample: [],
            approxLen: 0,
          }));
        }

        const candidates: ThinCandidate[] = [];
        const closePx = 18;

        for (const f of features) {
          const coords = f.geometry.coordinates;
          if (!coords || coords.length < 2) continue;

          // Sample a limited number of points for fast spacing checks.
          const stride = Math.max(1, Math.floor(coords.length / 80));
          const sample: Array<{ x: number; y: number }> = [];
          let minX = Infinity;
          let minY = Infinity;
          let maxX = -Infinity;
          let maxY = -Infinity;
          let approxLen = 0;
          let prev: { x: number; y: number } | null = null;

          for (let i = 0; i < coords.length; i += stride) {
            const c = coords[i];
            const lng = c?.[0];
            const lat = c?.[1];
            if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) continue;
            const p = map.project(c);
            if (!isFinitePoint(p)) continue;
            const pt = { x: p.x, y: p.y };
            sample.push(pt);
            minX = Math.min(minX, pt.x);
            minY = Math.min(minY, pt.y);
            maxX = Math.max(maxX, pt.x);
            maxY = Math.max(maxY, pt.y);
            if (prev) approxLen += dist(prev, pt);
            prev = pt;
          }

          if (sample.length < 2) continue;

          // Closed-loop detection: use *true* endpoints if possible (not sampled endpoints),
          // so we don't miss closure due to sampling stride.
          let isClosed = false;
          {
            const firstCoord = coords[0];
            const lastCoord = coords[coords.length - 1];
            const p0 = firstCoord ? map.project(firstCoord) : null;
            const p1 = lastCoord ? map.project(lastCoord) : null;
            if (p0 && p1 && isFinitePoint(p0) && isFinitePoint(p1)) {
              isClosed = dist({ x: p0.x, y: p0.y }, { x: p1.x, y: p1.y }) <= closePx;
            } else {
              const first = sample[0];
              const last = sample[sample.length - 1];
              isClosed = dist(first, last) <= closePx;
            }
          }

          const centroid = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

          candidates.push({
            feature: f,
            depth: f.properties.depth,
            isClosed,
            id: typeof f.properties.ID === 'number' ? f.properties.ID : null,
            centroid,
            bbox: { minX, minY, maxX, maxY },
            sample,
            approxLen,
          });
        }

        return candidates;
      };

      const thinForFishingHoles = (): ContourFeature[] => {
        if (minSeparationPx <= 0) {
          // Even when thinning is disabled (low zoom), preserve monotonic visibility:
          // anything we draw now must not disappear on the next zoom-in step.
          const s = stickyContoursRef.current;
          if (zoom >= s.maxZoom - 0.01) {
            s.maxZoom = Math.max(s.maxZoom, zoom);
            for (const f of features) {
              const id = typeof f.properties.ID === 'number' ? f.properties.ID : null;
              if (id != null) s.ids.add(id);
            }
          }
          return features;
        }

        const candidates = buildThinCandidates();
        if (candidates.length === 0) return features;

        // Priority:
        // 1) closed loops first (holes)
        // 2) deepest first (more negative depth)
        // 3) longer first
        candidates.sort((a, b) => {
          if (a.isClosed !== b.isClosed) return a.isClosed ? -1 : 1;
          if (a.depth !== b.depth) return a.depth - b.depth;
          return b.approxLen - a.approxLen;
        });

        const cellSize = 256;
        const key = (cx: number, cy: number) => `${cx},${cy}`;
        const grid = new Map<string, ThinCandidate[]>();

        const addToGrid = (c: ThinCandidate) => {
          const cx0 = Math.floor(c.bbox.minX / cellSize);
          const cy0 = Math.floor(c.bbox.minY / cellSize);
          const cx1 = Math.floor(c.bbox.maxX / cellSize);
          const cy1 = Math.floor(c.bbox.maxY / cellSize);
          for (let cy = cy0; cy <= cy1; cy++) {
            for (let cx = cx0; cx <= cx1; cx++) {
              const k = key(cx, cy);
              const arr = grid.get(k);
              if (arr) arr.push(c);
              else grid.set(k, [c]);
            }
          }
        };

        const nearbyAccepted = (c: ThinCandidate): ThinCandidate[] => {
          const cx0 = Math.floor(c.bbox.minX / cellSize);
          const cy0 = Math.floor(c.bbox.minY / cellSize);
          const cx1 = Math.floor(c.bbox.maxX / cellSize);
          const cy1 = Math.floor(c.bbox.maxY / cellSize);
          const out: ThinCandidate[] = [];
          for (let cy = cy0 - 1; cy <= cy1 + 1; cy++) {
            for (let cx = cx0 - 1; cx <= cx1 + 1; cx++) {
              const arr = grid.get(key(cx, cy));
              if (arr) out.push(...arr);
            }
          }
          return out;
        };

        const accepted: ThinCandidate[] = [];

        // Seed with sticky contours so they can never be dropped while zooming in.
        const stickyIds = stickyContoursRef.current.ids;
        for (const c of candidates) {
          if (c.id != null && stickyIds.has(c.id)) {
            accepted.push(c);
            addToGrid(c);
          }
        }

        for (const c of candidates) {
          if (c.id != null && stickyIds.has(c.id)) continue;
          let reject = false;
          const neighbors = nearbyAccepted(c);

          for (const n of neighbors) {
            // Closed-loop clustering: only compare closed loops that are actually near each other.
            if (c.isClosed && n.isClosed) {
              if (dist(c.centroid, n.centroid) > closedClusterRadiusPx) continue;
            } else {
              // For open lines, only thin against nearby depths.
              if (Math.abs(c.depth - n.depth) > maxDepthDiffForSpacing) continue;
            }

            if (!bboxIntersects(c.bbox, n.bbox, minSeparationPx)) continue;

            const d = minPointDistance(c.sample, n.sample, minSeparationPx);
            if (d <= minSeparationPx) {
              // Too close to an already-accepted contour.
              reject = true;
              break;
            }
          }

          if (reject) continue;
          accepted.push(c);
          addToGrid(c);
        }

        // Update sticky set only when zooming in / holding zoom (never on zoom-out).
        const s = stickyContoursRef.current;
        if (zoom >= s.maxZoom - 0.01) {
          s.maxZoom = Math.max(s.maxZoom, zoom);
          for (const a of accepted) {
            if (a.id != null) s.ids.add(a.id);
          }
        }

        return accepted.map((a) => a.feature);
      };

      const maxJumpPx = zoom >= 13 ? 220 : zoom >= 12 ? 300 : 420;

      // Extra safety: break segments on large *geographic* jumps too.
      // This catches rare cases where the data contains a discontinuity that
      // doesn't look huge in screen space (or where the line is undersampled).
      const maxJumpMeters = zoom >= 15 ? 600 : zoom >= 13 ? 1200 : zoom >= 11 ? 2500 : 6000;

      const haversineMeters = (a: [number, number], b: [number, number]) => {
        const toRad = (deg: number) => (deg * Math.PI) / 180;
        const R = 6371000;
        const lat1 = toRad(a[1]);
        const lat2 = toRad(b[1]);
        const dLat = lat2 - lat1;
        const dLon = toRad(b[0] - a[0]);
        const s1 = Math.sin(dLat / 2);
        const s2 = Math.sin(dLon / 2);
        const h = s1 * s1 + Math.cos(lat1) * Math.cos(lat2) * s2 * s2;
        return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
      };

      const drawFeatures = thinForFishingHoles();

      const aabbIntersects = (a: { minX: number; minY: number; maxX: number; maxY: number }, b: { minX: number; minY: number; maxX: number; maxY: number }) => {
        return !(a.maxX < b.minX || a.minX > b.maxX || a.maxY < b.minY || a.minY > b.maxY);
      };

      const angleBetween = (a: { x: number; y: number }, b: { x: number; y: number }) => {
        const dot = a.x * b.x + a.y * b.y;
        const ma = Math.sqrt(a.x * a.x + a.y * a.y);
        const mb = Math.sqrt(b.x * b.x + b.y * b.y);
        const denom = Math.max(1e-9, ma * mb);
        const cos = clamp(dot / denom, -1, 1);
        return Math.acos(cos);
      };

      const pointAtDistance = (points: { x: number; y: number }[], cumLen: number[], target: number) => {
        // Find segment where cumulative length crosses target.
        let lo = 1;
        let hi = cumLen.length - 1;
        while (lo < hi) {
          const mid = Math.floor((lo + hi) / 2);
          if (cumLen[mid] >= target) hi = mid;
          else lo = mid + 1;
        }
        const i = lo;
        const segLen = cumLen[i] - cumLen[i - 1];
        const t = segLen <= 1e-6 ? 0 : (target - cumLen[i - 1]) / segLen;
        const p0 = points[i - 1];
        const p1 = points[i];
        return { x: p0.x + (p1.x - p0.x) * t, y: p0.y + (p1.y - p0.y) * t, i };
      };

      const placeLabelsAlongPolyline = (points: { x: number; y: number }[], text: string, widthCss: number, heightCss: number) => {
        if (!shouldDrawLabels) return;
        if (points.length < 2) return;

        // Compute cumulative length.
        const cumLen: number[] = new Array(points.length);
        cumLen[0] = 0;
        for (let i = 1; i < points.length; i++) {
          cumLen[i] = cumLen[i - 1] + dist(points[i - 1], points[i]);
        }
        const length = cumLen[cumLen.length - 1];
        if (length < minLabelLineLengthPx) return;

        // Measure text for collision & on-screen checks.
        ctx.font = labelFont;
        const metrics = ctx.measureText(text);
        const textW = Math.max(8, metrics.width);
        const textH = 13; // matches font size

        const maxLabels = Math.min(3, Math.max(1, Math.floor(length / labelSpacingPx)));
        const step = length / (maxLabels + 1);

        for (let n = 1; n <= maxLabels; n++) {
          const target = step * n;
          const anchor = pointAtDistance(points, cumLen, target);
          const s = Math.min(40, Math.max(18, textW * 0.25));
          const back = pointAtDistance(points, cumLen, Math.max(0, target - s));
          const front = pointAtDistance(points, cumLen, Math.min(length, target + s));

          const v1 = { x: anchor.x - back.x, y: anchor.y - back.y };
          const v2 = { x: front.x - anchor.x, y: front.y - anchor.y };
          const bendRad = angleBetween(v1, v2);
          const bendDeg = (bendRad * 180) / Math.PI;
          if (bendDeg > labelMinTurnDeg) continue;

          let angle = Math.atan2(front.y - back.y, front.x - back.x);
          if (angle > Math.PI / 2 || angle < -Math.PI / 2) angle += Math.PI;

          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          const halfW = textW / 2 + labelPaddingPx;
          const halfH = textH / 2 + labelPaddingPx;
          const hx = Math.abs(cos) * halfW + Math.abs(sin) * halfH;
          const hy = Math.abs(sin) * halfW + Math.abs(cos) * halfH;
          const aabb = { minX: anchor.x - hx, maxX: anchor.x + hx, minY: anchor.y - hy, maxY: anchor.y + hy };

          // Keep labels on-screen to avoid "floating" in empty areas.
          const margin = 8;
          if (aabb.minX < margin || aabb.maxX > widthCss - margin || aabb.minY < margin || aabb.maxY > heightCss - margin) {
            continue;
          }

          // Collision check.
          const collide = placedLabelAabbs.some((b) => aabbIntersects(aabb, b));
          if (collide) continue;

          labels.push({ x: anchor.x, y: anchor.y, angle, text, aabb });
          placedLabelAabbs.push(aabb);
        }
      };

      const approxPointCount = drawFeatures.reduce((sum, f) => sum + (f.geometry.coordinates?.length ?? 0), 0);

      // Note: We intentionally avoid smoothing here because we need robust
      // segment termination to prevent long straight “teleport” lines.

      for (const feature of drawFeatures) {
        const coords = feature.geometry.coordinates;
        if (!coords || coords.length < 2) continue;

        const depthText = `${Math.abs(feature.properties.depth)}m`;
        const color = getDepthColor(feature.properties.depth);

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;

        let segment: { x: number; y: number }[] = [];
        let prev: { x: number; y: number } | null = null;
        let prevCoord: [number, number] | null = null;

        const drawSegmentPath = (pts: { x: number; y: number }[]) => {
          if (pts.length < 2) return;

          // At very high zoom, contours can look jagged due to discrete vertex sampling.
          // Smooth by drawing quadratic Béziers through midpoints in screen space.
          // Important: handle *closed* loops with a cyclic algorithm so the end meets the start.
          if (shouldSmoothLines && pts.length >= 4) {
            const closePx = 10;
            const first = pts[0];
            const last = pts[pts.length - 1];
            const isClosed = dist(first, last) <= closePx;

            // If the data explicitly repeats the first point at the end, drop the duplicate.
            const basePts = isClosed ? pts.slice(0, -1) : pts;

            if (isClosed && basePts.length >= 3) {
              const n = basePts.length;
              const mid = (a: { x: number; y: number }, b: { x: number; y: number }) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
              const mids = new Array<{ x: number; y: number }>(n);
              for (let i = 0; i < n; i++) {
                mids[i] = mid(basePts[i], basePts[(i + 1) % n]);
              }

              ctx.beginPath();
              // Start at midpoint between last and first.
              ctx.moveTo(mids[n - 1].x, mids[n - 1].y);
              for (let i = 0; i < n; i++) {
                ctx.quadraticCurveTo(basePts[i].x, basePts[i].y, mids[i].x, mids[i].y);
              }
              ctx.closePath();
              ctx.stroke();
              return;
            }

            // Open polyline smoothing.
            if (!isClosed) {
              ctx.beginPath();
              ctx.moveTo(basePts[0].x, basePts[0].y);

              for (let i = 1; i < basePts.length - 2; i++) {
                const xc = (basePts[i].x + basePts[i + 1].x) / 2;
                const yc = (basePts[i].y + basePts[i + 1].y) / 2;
                ctx.quadraticCurveTo(basePts[i].x, basePts[i].y, xc, yc);
              }

              const n = basePts.length;
              ctx.quadraticCurveTo(basePts[n - 2].x, basePts[n - 2].y, basePts[n - 1].x, basePts[n - 1].y);
              ctx.stroke();
              return;
            }
          }

          ctx.beginPath();
          ctx.moveTo(pts[0].x, pts[0].y);
          for (let i = 1; i < pts.length; i++) {
            ctx.lineTo(pts[i].x, pts[i].y);
          }
          ctx.stroke();
        };

        const flush = () => {
          if (segment.length < 2) {
            segment = [];
            prev = null;
            prevCoord = null;
            return;
          }

          drawSegmentPath(segment);
          drawnLines++;

          placeLabelsAlongPolyline(segment, depthText, widthCss, heightCss);

          segment = [];
          prev = null;
          prevCoord = null;
        };

        for (let i = 0; i < coords.length; i++) {
          const coord = coords[i];
          const lng = coord?.[0];
          const lat = coord?.[1];
          if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
            flush();
            continue;
          }

          const p = map.project(coord);
          if (!isFinitePoint(p)) {
            flush();
            continue;
          }

          totalPoints++;

          if (prev && prevCoord) {
            const jumpPx = dist(prev, p);
            const jumpM = haversineMeters(prevCoord, [lng, lat]);
            // Teleport-line mitigation:
            // Only break when the discontinuity is large in *both* screen space and
            // geography. Using OR was too aggressive at high zoom and caused normal
            // (undersampled) contours to fragment into many short segments.
            const isTeleport = jumpPx > maxJumpPx && jumpM > maxJumpMeters;
            // Absolute guardrail for pathological screen-space jumps.
            const isHugeScreenJump = jumpPx > Math.max(1200, maxJumpPx * 6);
            if (isTeleport || isHugeScreenJump) {
              flush();
            }
          }

          segment.push({ x: p.x, y: p.y });
          prev = { x: p.x, y: p.y };
          prevCoord = [lng, lat];
        }

        flush();
      }

      // Draw labels *after* all lines so they sit on top.
      if (shouldDrawLabels && labels.length > 0) {
        ctx.font = labelFont;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        for (const l of labels) {
          ctx.save();
          ctx.translate(l.x, l.y);
          ctx.rotate(l.angle);

          // Knock out the line under the label so the text reads as "on" the contour.
          ctx.globalCompositeOperation = 'destination-out';
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = labelKnockoutWidth;
          ctx.strokeText(l.text, 0, 0);
          ctx.globalCompositeOperation = 'source-over';

          ctx.strokeStyle = labelHaloColor;
          ctx.lineWidth = labelHaloWidth;
          ctx.strokeText(l.text, 0, 0);

          ctx.fillStyle = labelTextColor;
          ctx.fillText(l.text, 0, 0);

          ctx.restore();
        }
      }

      const cInfo = lastContextQueryRef.current;
      const dInfo = lastDetailQueryRef.current;
      const w = lastQueryWindowRef.current;
      if (showDebug) setDebugInfo((prev) => {
        const lines: string[] = [];
        lines.push(`Zoom: ${zoom.toFixed(2)}`);
        if (cInfo) lines.push(`Fetch(context): ${cInfo.count} in ${cInfo.ms}ms (mod ${cInfo.depthModulo})`);
        if (dInfo) lines.push(`Fetch(detail): ${dInfo.count} in ${dInfo.ms}ms (mod ${dInfo.depthModulo})`);
        if (w) {
          lines.push(
            `Query(context): ${w.context.spanLon.toFixed(3)}°x${w.context.spanLat.toFixed(3)}° | Query(detail): ${w.detail.spanLon.toFixed(3)}°x${w.detail.spanLat.toFixed(3)}°`
          );
        }
        if (lastQueryMetaRef.current) {
          lines.push(
            `Query(minZoom): context ${lastQueryMetaRef.current.minContextZoom}, detail ${lastQueryMetaRef.current.minDetailZoom}`
          );
        }
        lines.push(
          `Draw: ${drawnLines} segments, ~${approxPointCount.toLocaleString()} pts${shouldDrawLabels ? '' : ' (no labels)'}${
            minSeparationPx > 0 && drawFeatures.length !== features.length
              ? ` (thinned ${features.length}→${drawFeatures.length})`
              : ''
          }`
        );
        if (minSeparationPx > 0) {
          lines.push(
            `Thin: minSep ${minSeparationPx}px, depthΔ<=${maxDepthDiffForSpacing}m, closedRadius ${closedClusterRadiusPx}px`
          );
        }
        lines.push(
          `Sticky: ${stickyContoursRef.current.ids.size} ids (maxZoom ${stickyContoursRef.current.maxZoom.toFixed(1)})`
        );
        return [...prev.slice(0, 3), ...lines];
      });
    };
  }, [map, visible, features, smoothLines]);

  // Build the fetch function whenever map/visible changes.
  useEffect(() => {
    const combineContextAndDetail = (): ContourFeature[] => {
      const context = contextFeaturesRef.current;
      const detail = detailFeaturesRef.current;

      // Prefer detail over context for the same contour ID so we don't double-draw.
      const contextNoId: ContourFeature[] = [];
      const detailNoId: ContourFeature[] = [];
      // NOTE: a single logical contour (FeatureId) can arrive as multiple segments because
      // the server clips to the query polygon and expands MultiLineString parts.
      // So we must keep *all* segments per ID.
      const contextById = new Map<number, ContourFeature[]>();
      const detailById = new Map<number, ContourFeature[]>();

      for (const f of context) {
        const id = f?.properties?.ID;
        if (typeof id === 'number') {
          const arr = contextById.get(id);
          if (arr) arr.push(f);
          else contextById.set(id, [f]);
        } else {
          contextNoId.push(f);
        }
      }
      for (const f of detail) {
        const id = f?.properties?.ID;
        if (typeof id === 'number') {
          const arr = detailById.get(id);
          if (arr) arr.push(f);
          else detailById.set(id, [f]);
        } else {
          detailNoId.push(f);
        }
      }

      const combined: ContourFeature[] = [...contextNoId];
      for (const [id, segs] of contextById) {
        // If any detail segments exist for this contour, drop all context segments.
        if (!detailById.has(id)) combined.push(...segs);
      }
      // Draw detail *after* context so it sits on top.
      combined.push(...detailNoId);
      for (const [, segs] of detailById) combined.push(...segs);
      return combined;
    };

    const startFetch = async (
      kind: 'context' | 'detail',
      padded: Bounds,
      zoom: number,
      depthModulo: number,
      signal: AbortSignal
    ) => {
      const params = new URLSearchParams({
        west: String(padded.west),
        south: String(padded.south),
        east: String(padded.east),
        north: String(padded.north),
        zoom: String(zoom),
        depthModulo: String(depthModulo),
      });

      // Note: keep server-side clipping enabled by default; it reduces geometry
      // size significantly at mid zoom, which often improves overall latency.

      const started = performance.now();
      const resp = await fetch(`/api/bathymetry?${params.toString()}`, { signal });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = (await resp.json()) as ContourFeatureCollection;

      const elapsed = Math.round(performance.now() - started);
      const info = {
        count: json.features?.length ?? 0,
        ms: typeof json.info?.queryTimeMs === 'number' ? json.info.queryTimeMs : elapsed,
        zoom,
        depthModulo,
      };
      const list = Array.isArray(json.features) ? json.features : [];

      if (kind === 'detail') {
        lastDetailQueryRef.current = info;
        detailFeaturesRef.current = list;
      } else {
        lastContextQueryRef.current = info;
        contextFeaturesRef.current = list;
      }

      return { kind, info, list };
    };

    requestFetchRef.current = (opts) => {
      if (!map || !visible) return;

      const b = map.getBounds();
      const zoom = map.getZoom();

      const rawView: Bounds = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      };

      // Two-pass fetch:
      // - DETAIL: smaller bounded window, high resolution (depthModulo pinned to actual zoom)
      // - CONTEXT: wider bounded window, coarse depthModulo so it stays fast
      const center = map.getCenter();

      // At low zoom, users expect contours across the *entire* viewport.
      // Our physical caps (tens of km) can unintentionally clamp the query to a
      // smaller area around the center, causing visible gaps near the viewport
      // edges (including areas under UI overlays). If the viewport itself is
      // larger than the capped context window, switch to a viewport-wide query.
      const rawSizeM = boundsMeters(rawView, center.lat);
      const useViewportWideQuery = zoom < 10 || rawSizeM.widthM > 70_000 || rawSizeM.heightM > 55_000;

      if (useViewportWideQuery) {
        // Single-pass behavior (detail only): query the viewport (padded) and draw it.
        // Keep modulo coarse at low zoom to avoid huge row counts.
        const base = padBounds(rawView, zoom < 8.5 ? 0.09 : 0.07);
        const wideView = snapBounds(base, zoom < 8.5 ? 0.05 : 0.02);

        {
          const s = boundsSpan(wideView);
          lastQueryWindowRef.current = {
            detail: { spanLon: s.lon, spanLat: s.lat },
            context: { spanLon: s.lon, spanLat: s.lat },
          };
        }
        lastQueryMetaRef.current = { minDetailZoom: 0, minContextZoom: 0 };

        // Clear any stale context so it doesn't keep rendering when we skip context fetch.
        contextFeaturesRef.current = [];
        lastContextQueryRef.current = null;

        const depthModuloDetail = getDepthModulo(zoom, wideView);
        const paddedDetail = padBounds(wideView, 0.04);

        const cachedDetail = detailCacheRef.current;
        // IMPORTANT: data is server-clipped to the padded query window.
        // If we reuse cached data when the new padded window isn't fully contained,
        // contours will appear to "break" near the viewport edges until you pan far enough.
        const hasDetailHit =
          !!cachedDetail && cachedDetail.depthModulo === depthModuloDetail && contains(cachedDetail.padded, paddedDetail);
        if (hasDetailHit) {
          awaitingFreshDataRef.current = false;
          setDebugInfo((prev) => [...prev.slice(0, 4), 'Fetch: cache hit (detail)']);
          return;
        }

        // Blank until new data arrives.
        awaitingFreshDataRef.current = true;
        clearOverlayRef.current();
        setFeatures([]);

        detailCacheRef.current = { padded: paddedDetail, depthModulo: depthModuloDetail };

        const delayMs = opts?.immediate ? 0 : 120;
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = null;
        }

        fetchTimeoutRef.current = setTimeout(async () => {
          abortDetailRef.current?.abort();
          abortContextRef.current?.abort();
          const abortDetail = new AbortController();
          abortDetailRef.current = abortDetail;
          abortContextRef.current = null;

          try {
            await startFetch('detail', paddedDetail, zoom, depthModuloDetail, abortDetail.signal);
            awaitingFreshDataRef.current = false;
            setFeatures(combineContextAndDetail());
          } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
              clearFetchCache();
              return;
            }
            clearFetchCache();
            setDebugInfo((prev) => [...prev.slice(0, 4), `Fetch error: ${e instanceof Error ? e.message : String(e)}`]);
            detailFeaturesRef.current = [];
            setFeatures([]);
            awaitingFreshDataRef.current = false;
          }
        }, delayMs);

        return;
      }

      // Detail needs enough surrounding geometry so contours don't look like short, disconnected
      // fragments due to server-side clipping. We achieve this by expanding the detail query
      // window to behave like a minimum zoom level (e.g. z14) even when you're at z16.
      // This keeps “dots to connect” outside the immediate viewport without going crazy like z5.
      const MIN_DETAIL_QUERY_ZOOM = 14;
      const detailScale = zoom > MIN_DETAIL_QUERY_ZOOM ? Math.pow(2, zoom - MIN_DETAIL_QUERY_ZOOM) : 1;
      const detailRaw = expandBoundsAboutCenter(rawView, detailScale);
      let detailView: Bounds = normalizeQueryWindow(detailRaw, center.lat, {
        minWidthM: 6_000,
        minHeightM: 4_500,
        // Cap to avoid huge result sets at very high zoom.
        maxWidthM: 35_000,
        maxHeightM: 28_000,
      });
      detailView = zoom >= 15 ? snapBounds(detailView, 0.002) : detailView;

      const MIN_CONTEXT_QUERY_ZOOM = 14;
      const zoomForContext = Math.min(zoom, MIN_CONTEXT_QUERY_ZOOM);
      const scale = zoom > zoomForContext ? Math.pow(2, zoom - zoomForContext) : 1;
      let contextView: Bounds = expandBoundsAboutCenter(rawView, scale);
      contextView = normalizeQueryWindow(contextView, center.lat, {
        minWidthM: 18_000,
        minHeightM: 14_000,
        maxWidthM: 70_000,
        maxHeightM: 55_000,
      });
      contextView = zoom >= 15 ? snapBounds(contextView, 0.01) : contextView;

      {
        const d = boundsSpan(detailView);
        const c = boundsSpan(contextView);
        lastQueryWindowRef.current = {
          detail: { spanLon: d.lon, spanLat: d.lat },
          context: { spanLon: c.lon, spanLat: c.lat },
        };

        lastQueryMetaRef.current = { minDetailZoom: MIN_DETAIL_QUERY_ZOOM, minContextZoom: MIN_CONTEXT_QUERY_ZOOM };
      }

      // Keep detail resolution pinned to actual zoom so “holes” don’t disappear.
      // NOTE: forcing modulo=2 too early makes mid-zoom views overwhelmingly dense.
      // Only request the densest contours once you're properly zoomed in.
      const depthModuloDetail = zoom >= 13.5 ? 2 : getDepthModulo(zoom, detailView);
      // Context is intentionally coarse to avoid 10s of thousands of rows.
      const depthModuloContext = Math.max(depthModuloDetail * 2, 10, getDepthModulo(zoomForContext, contextView));

      const paddedDetail = zoom >= 15 ? padBounds(detailView, 0.03) : padBounds(detailView, 0.06);
      const paddedContext = zoom >= 15 ? padBounds(contextView, 0.02) : padBounds(contextView, 0.04);

      const cachedDetail = detailCacheRef.current;
      const cachedContext = contextCacheRef.current;
      // See note above: must compare against the padded query windows (server clipping).
      const hasDetailHit =
        !!cachedDetail && cachedDetail.depthModulo === depthModuloDetail && contains(cachedDetail.padded, paddedDetail);
      const hasContextHit =
        !!cachedContext && cachedContext.depthModulo === depthModuloContext && contains(cachedContext.padded, paddedContext);

      if (hasDetailHit && hasContextHit) {
        awaitingFreshDataRef.current = false;
        setDebugInfo((prev) => [...prev.slice(0, 4), 'Fetch: cache hit (detail+context)']);
        return;
      }

      // Blank until the new pair of queries completes, otherwise zoom-out can briefly
      // show the previous high-resolution contours.
      awaitingFreshDataRef.current = true;
      clearOverlayRef.current();
      setFeatures([]);

      detailCacheRef.current = { padded: paddedDetail, depthModulo: depthModuloDetail };
      contextCacheRef.current = { padded: paddedContext, depthModulo: depthModuloContext };

      const delayMs = opts?.immediate ? 0 : 120;
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }

      fetchTimeoutRef.current = setTimeout(async () => {
        abortDetailRef.current?.abort();
        abortContextRef.current?.abort();
        const abortDetail = new AbortController();
        const abortContext = new AbortController();
        abortDetailRef.current = abortDetail;
        abortContextRef.current = abortContext;

        try {
          const tasks: Promise<void>[] = [];
          if (!hasContextHit) tasks.push(startFetch('context', paddedContext, zoom, depthModuloContext, abortContext.signal).then(() => {}));
          if (!hasDetailHit) tasks.push(startFetch('detail', paddedDetail, zoom, depthModuloDetail, abortDetail.signal).then(() => {}));
          await Promise.all(tasks);
          awaitingFreshDataRef.current = false;
          setFeatures(combineContextAndDetail());
        } catch (e) {
          if (e instanceof DOMException && e.name === 'AbortError') {
            // Aborted fetch did not produce data; don't keep a cache entry that blocks retries.
            clearFetchCache();
            return;
          }
          // Allow a retry on next interaction if the fetch failed.
          clearFetchCache();
          setDebugInfo((prev) => [...prev.slice(0, 4), `Fetch error: ${e instanceof Error ? e.message : String(e)}`]);
          contextFeaturesRef.current = [];
          detailFeaturesRef.current = [];
          setFeatures([]);
          awaitingFreshDataRef.current = false;
        }
      }, delayMs);
    };
  }, [map, visible]);

  // Draw immediately whenever fetched features change.
  useEffect(() => {
    if (!map || !visible) return;
    // Don't redraw while the map is mid-interaction; it can create warped/laggy visuals.
    if (isInteractingRef.current || map.isMoving()) {
      pendingDrawRef.current = true;
      return;
    }

    pendingDrawRef.current = false;
    const raf = requestAnimationFrame(() => drawNowRef.current());
    return () => cancelAnimationFrame(raf);
  }, [features, map, visible]);

  // Redraw immediately when line smoothing toggles.
  useEffect(() => {
    if (!map || !visible) return;
    if (isInteractingRef.current || map.isMoving()) {
      pendingDrawRef.current = true;
      return;
    }

    pendingDrawRef.current = false;
    const raf = requestAnimationFrame(() => drawNowRef.current());
    return () => cancelAnimationFrame(raf);
  }, [smoothLines, map, visible]);

  // Register map listeners once per map instance.
  useEffect(() => {
    if (!map || !canvasRef.current) return;

    const canvas = canvasRef.current;

    const clearOverlay = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const mapCanvas = map.getCanvas();
      const container = mapCanvas;
      const dpr = window.devicePixelRatio || 1;
      const widthCss = Math.max(1, container.clientWidth);
      const heightCss = Math.max(1, container.clientHeight);
      const widthPx = Math.max(1, Math.floor(widthCss * dpr));
      const heightPx = Math.max(1, Math.floor(heightCss * dpr));

      if (canvas.width !== widthPx || canvas.height !== heightPx) {
        canvas.width = widthPx;
        canvas.height = heightPx;
        canvas.style.width = `${widthCss}px`;
        canvas.style.height = `${heightCss}px`;
      }

      canvas.style.transform = mapCanvas.style.transform || '';
      canvas.style.transformOrigin = '0 0';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, widthCss, heightCss);
    };

    clearOverlayRef.current = clearOverlay;

    const onMoveStart = () => {
      if (!visible) return;
      isInteractingRef.current = true;
      // If we can't be instant, don't lag: blank during interaction.
      abortDetailRef.current?.abort();
      abortContextRef.current?.abort();
      clearFetchCache();
      clearOverlay();
    };

    const onZoomStart = () => {
      if (!visible) return;
      isInteractingRef.current = true;
      abortDetailRef.current?.abort();
      abortContextRef.current?.abort();
      clearFetchCache();
      clearOverlay();
    };

    const onMove = () => {
      // Keep blank while moving.
      if (!visible) return;
      if (isInteractingRef.current) clearOverlay();
    };

    const onZoom = () => {
      // Some zoom interactions don't reliably fire zoomstart; keep blank here too.
      if (!visible) return;
      isInteractingRef.current = true;
      if (isInteractingRef.current) clearOverlay();
    };

    const onMoveEnd = () => {
      isInteractingRef.current = false;
      // Skip fetch if zoomend just fired (<200ms ago). MapLibre always fires zoomend
      // then moveend ~1ms apart on every zoom, which would otherwise double the SQL
      // query and React draw cycle on every zoom interaction.
      if (Date.now() - lastZoomEndTimeRef.current < 200) return;
      // Fetch immediately for the final bounds; draw happens when features arrive.
      requestFetchRef.current({ immediate: true });
      // Keep blank until fresh data arrives (prevents stale high-res artifacts on zoom-out).
      if (awaitingFreshDataRef.current) {
        pendingDrawRef.current = true;
        clearOverlay();
      }
    };
    const onZoomEnd = () => {
      isInteractingRef.current = false;
      lastZoomEndTimeRef.current = Date.now();
      requestFetchRef.current({ immediate: true });
      if (awaitingFreshDataRef.current) {
        pendingDrawRef.current = true;
        clearOverlay();
      }
    };
    const onResize = () => {
      if (!visible) return;
      // If we're not interacting, redraw; otherwise keep blank.
      if (!isInteractingRef.current && !map.isMoving()) {
        drawNowRef.current();
      } else {
        clearOverlay();
      }
    };

    const onIdle = () => {
      if (!visible) return;
      // When MapLibre reports it's settled, draw if we deferred a draw while moving.
      isInteractingRef.current = false;
      if (pendingDrawRef.current && !map.isMoving() && !awaitingFreshDataRef.current) {
        pendingDrawRef.current = false;
        drawNowRef.current();
      }
    };

    map.on('movestart', onMoveStart);
    map.on('zoomstart', onZoomStart);
    map.on('move', onMove);
    map.on('zoom', onZoom);
    map.on('moveend', onMoveEnd);
    map.on('zoomend', onZoomEnd);
    map.on('resize', onResize);
    map.on('idle', onIdle);

    // Initial load
    requestFetchRef.current();
    drawNowRef.current();

    return () => {
      map.off('movestart', onMoveStart);
      map.off('zoomstart', onZoomStart);
      map.off('move', onMove);
      map.off('zoom', onZoom);
      map.off('moveend', onMoveEnd);
      map.off('zoomend', onZoomEnd);
      map.off('resize', onResize);
      map.off('idle', onIdle);
    };
  }, [map, visible]);

  // When visibility toggles off, cancel in-flight work and clear.
  useEffect(() => {
    if (visible) return;
    abortDetailRef.current?.abort();
    abortContextRef.current?.abort();
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    detailCacheRef.current = null;
    contextCacheRef.current = null;
    detailFeaturesRef.current = [];
    contextFeaturesRef.current = [];
    setFeatures([]);

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <>
      {canvasContainer
        ? createPortal(
            <canvas
              ref={canvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 10,
              }}
            />,
            canvasContainer
          )
        : null}

      {showDebug && (
        <div
          style={{
            position: 'absolute',
            bottom: 40,
            left: 10,
            background: 'rgba(0,0,0,0.9)',
            color: '#0f0',
            padding: '8px 12px',
            fontSize: '11px',
            fontFamily: 'monospace',
            zIndex: 11,
            maxWidth: '480px',
            whiteSpace: 'pre-wrap',
          }}
        >
          <div style={{ color: '#ff0', marginBottom: 4 }}>CANVAS DEBUG</div>
          {debugInfo.slice(-6).map((line, i) => (
            <div key={i} style={{ color: line.startsWith('Fetch error') ? '#f66' : '#fff' }}>
              {line}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
